import React, { useEffect, useRef, useState } from "react";
import {
    addDoc,
    collection,
    doc,
    getDoc,
    onSnapshot,
    runTransaction,
    serverTimestamp,
  } from "firebase/firestore";
import { getIdToken } from "firebase/auth";
import { db, auth } from "../firebase";

export default function NotificationCenterPage({
    auctions = [],
    users = [],
  }) {
    const [auctionSearch, setAuctionSearch] = useState("");
    const [selectedAuctionId, setSelectedAuctionId] = useState("");
    const [selectedCategories, setSelectedCategories] = useState([]);

    const [userSearch, setUserSearch] = useState("");
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [excludedUserIds, setExcludedUserIds] = useState([]);
    const [excludedUserSearch, setExcludedUserSearch] = useState("");

    const [officialCategories, setOfficialCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState("");
  
    const [audience, setAudience] = useState({
    allUsers: false,
    smartCategory: false,
    individualUsers: false,
  });

  const [campaignName, setCampaignName] = useState("");
  const [englishTitle, setEnglishTitle] = useState("");
  const [englishBody, setEnglishBody] = useState("");
  const [arabicTitle, setArabicTitle] = useState("");
  const [arabicBody, setArabicBody] = useState("");

  const [destination, setDestination] = useState("browse");
  const [fullMessageEnglish, setFullMessageEnglish] = useState("");
  const [fullMessageArabic, setFullMessageArabic] = useState("");

  const [deliveryMode, setDeliveryMode] = useState("draft");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaveError, setDraftSaveError] = useState("");
  const [draftSaveSuccess, setDraftSaveSuccess] = useState("");

    // Test Send has independent processing and result states.
    const [testSending, setTestSending] = useState(false);
    const [testSendError, setTestSendError] = useState("");
    const [testSendSuccess, setTestSendSuccess] = useState("");
  
    // Send Now has separate processing and result states.
    const [sendNowProcessing, setSendNowProcessing] = useState(false);
    const [sendNowError, setSendNowError] = useState("");
    const [sendNowSuccess, setSendNowSuccess] = useState("");

  const [editingDraftId, setEditingDraftId] = useState(null);

  const [savedDrafts, setSavedDrafts] = useState([]);
  const [savedDraftsLoading, setSavedDraftsLoading] = useState(true);
  const [savedDraftsError, setSavedDraftsError] = useState("");

  const [campaignHistory, setCampaignHistory] = useState([]);
  const [campaignHistoryLoading, setCampaignHistoryLoading] = useState(true);
  const [campaignHistoryError, setCampaignHistoryError] = useState("");

  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [deleteDraftError, setDeleteDraftError] = useState("");

  const [audiencePreviewCount, setAudiencePreviewCount] = useState(null);
  const [audiencePreviewLoading, setAudiencePreviewLoading] = useState(false);
  const [audiencePreviewError, setAudiencePreviewError] = useState("");
  const audiencePreviewRequestId = useRef(0);

  useEffect(() => {
    const campaignsRef = collection(db, "notificationCampaigns");

    const unsubscribe = onSnapshot(
      campaignsRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (snapshot.metadata.fromCache) {
            setSavedDraftsLoading(true);
            setCampaignHistoryLoading(true);
  
            setSavedDraftsError(
              "Waiting for Firebase to confirm saved drafts."
            );
            setCampaignHistoryError(
              "Waiting for Firebase to confirm campaign history."
            );
            return;
          }
  
          const campaigns = snapshot.docs.map((campaignDoc) => ({
            id: campaignDoc.id,
            ...campaignDoc.data(),
          }));
  
          const sortNewestFirst = (a, b) =>
            (b.createdAt?.toMillis?.() || 0) -
            (a.createdAt?.toMillis?.() || 0);
  
          const drafts = campaigns
            .filter((campaign) => campaign.status === "draft")
            .sort(sortNewestFirst);
  
          const history = campaigns
            .filter((campaign) => campaign.status !== "draft")
            .sort(sortNewestFirst);
  
          setSavedDrafts(drafts);
          setSavedDraftsLoading(false);
          setSavedDraftsError("");
  
          setCampaignHistory(history);
          setCampaignHistoryLoading(false);
          setCampaignHistoryError("");
        },
        (error) => {
          setSavedDraftsLoading(false);
          setCampaignHistoryLoading(false);
  
          setSavedDraftsError(
            error.message || "Unable to load saved drafts."
          );
          setCampaignHistoryError(
            error.message || "Unable to load campaign history."
          );
        }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    audiencePreviewRequestId.current += 1;
    setAudiencePreviewCount(null);
    setAudiencePreviewError("");
    setAudiencePreviewLoading(false);
  }, [
    audience.allUsers,
    audience.smartCategory,
    audience.individualUsers,
    selectedCategories,
    selectedUserIds,
    excludedUserIds,
  ]);

  useEffect(() => {
    const categoriesRef = collection(db, "auctionCategories");

    const unsubscribe = onSnapshot(
      categoriesRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (snapshot.metadata.fromCache) {
          setCategoriesLoading(true);
          setCategoriesError(
            "Waiting for Firebase to confirm the current category list."
          );
          return;
        }

        const categoryList = snapshot.docs
          .map((categoryDoc) => ({
            id: categoryDoc.id,
            ...categoryDoc.data(),
          }))
          .filter(
            (category) =>
              category.active === true &&
              typeof category.name === "string" &&
              category.name.trim() !== ""
          )
          .sort((a, b) => {
            if (a.id === "others") return 1;
            if (b.id === "others") return -1;

            const orderA = Number.isSafeInteger(a.order)
              ? a.order
              : Number.MAX_SAFE_INTEGER;

            const orderB = Number.isSafeInteger(b.order)
              ? b.order
              : Number.MAX_SAFE_INTEGER;

            return orderA - orderB || a.id.localeCompare(b.id);
          });

          const activeCategoryNames = new Set(
            categoryList.map((category) => category.name)
          );
  
          setSelectedCategories((previous) =>
            previous.filter((name) => activeCategoryNames.has(name))
          );
  
          setOfficialCategories(categoryList);
          setCategoriesLoading(false);
          setCategoriesError("");
      },
      (error) => {
        console.error(
          "[NotificationCenter] Failed to load categories:",
          error
        );

        setOfficialCategories([]);
        setCategoriesLoading(false);
        setCategoriesError(
          "Unable to load official categories from Firebase."
        );
      }
    );

    return () => unsubscribe();
  }, []);

  const toggleAudience = (key) => {
    setAudience((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const normalizedUserSearch = userSearch.trim().toLowerCase();

  const matchingUsers = normalizedUserSearch
  ? users.filter((user) => {
      const username = String(user.username || "").toLowerCase();
      const email = String(user.email || "").toLowerCase();

      return (
        username.includes(normalizedUserSearch) ||
        email.includes(normalizedUserSearch)
      );
    })
  : [];

const normalizedExcludedUserSearch = excludedUserSearch
  .trim()
  .toLowerCase();

  const matchingExcludedUsers = normalizedExcludedUserSearch
  ? users.filter((user) => {
      const username = String(user.username || "").toLowerCase();
      const email = String(user.email || "").toLowerCase();

      return (
        username.includes(normalizedExcludedUserSearch) ||
        email.includes(normalizedExcludedUserSearch)
      );
    })
  : [];

const excludedUserIdSet = new Set(excludedUserIds);

const individualRecipientIds = audience.individualUsers
  ? selectedUserIds
  : [];

const allUserRecipientIds = audience.allUsers
  ? users.map((user) => user.id)
  : [];

const uniqueRecipientIds = [
  ...new Set([
    ...allUserRecipientIds,
    ...individualRecipientIds,
  ]),
].filter(
    (userId) =>
      userId &&
      !excludedUserIdSet.has(userId)
  );
  
  const handlePreviewAudience = async () => {
    const requestId = ++audiencePreviewRequestId.current;

    setAudiencePreviewLoading(true);
    setAudiencePreviewError("");
    setAudiencePreviewCount(null);

    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("Please sign in again.");
      }

      const idToken = await getIdToken(currentUser);

      if (requestId !== audiencePreviewRequestId.current) {
        return;
      }

      const response = await fetch(
        "https://us-central1-q8auctionapp-2c6dd.cloudfunctions.net/adminPreviewNotificationAudience",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            allUsers: audience.allUsers,
            smartCategory: audience.smartCategory,
            individualUsers: audience.individualUsers,
            selectedCategories,
            selectedUserIds,
            excludedUserIds,
          }),
        }
      );

      const result = await response.json();

      if (requestId !== audiencePreviewRequestId.current) {
        return;
      }

      if (!response.ok || result.ok !== true) {
        throw new Error(
          result.error || "Unable to calculate audience."
        );
      }

      if (
        !Number.isSafeInteger(result.recipientCount) ||
        result.recipientCount < 0
      ) {
        throw new Error("Invalid audience count returned.");
      }

      setAudiencePreviewCount(result.recipientCount);
    } catch (error) {
      if (requestId === audiencePreviewRequestId.current) {
        setAudiencePreviewError(
          error.message || "Audience preview failed."
        );
      }
    } finally {
      if (requestId === audiencePreviewRequestId.current) {
        setAudiencePreviewLoading(false);
      }
    }
};

// Test Send — sends only to the signed-in owner using a saved draft.
// The backend controls whether actual delivery is enabled.
const handleTestSend = async () => {
  if (testSending || draftSaving || deletingDraftId !== null) {
    return;
  }

  setTestSending(true);
  setTestSendError("");
  setTestSendSuccess("");

  try {
    if (!editingDraftId) {
      throw new Error(
        "Please select and save a notification draft before testing."
      );
    }

    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("Please sign in again.");
    }

    const idToken = await getIdToken(currentUser);

    const response = await fetch(
      "https://us-central1-q8auctionapp-2c6dd.cloudfunctions.net/adminTestSendNotification",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
            campaignId: editingDraftId,
            recipientUid: "ILRSkm9iyXP3P9m2ARHtZdISWZf1",
          }),
      }
    );

    const result = await response.json();

    if (response.status === 202 && result.status === "uncertain") {
        throw new Error(
          result.message ||
            "Test Send delivery is uncertain. Do not retry until the attempt has been checked."
        );
      }
  
      if (!response.ok || result.ok !== true) {
        throw new Error(
          result.error || "Test Send could not be completed."
        );
      }
  
      if (result.expoAccepted !== true) {
        throw new Error(
          result.reason ||
            "Expo did not accept the Test Send notification."
        );
      }
  
      setTestSendSuccess(
        "Test notification accepted by Expo. Please check your phone."
      );
  } catch (error) {
    setTestSendError(
      error.message || "Test Send failed. Please try again."
    );
  } finally {
    setTestSending(false);
  }
};

// Send Now — processes an existing saved draft only.
// The backend independently enforces the approved recipient UID.
const handleSendNow = async () => {
    if (
      sendNowProcessing ||
      draftSaving ||
      testSending ||
      deletingDraftId !== null
    ) {
      return;
    }
  
    setSendNowProcessing(true);
    setSendNowError("");
    setSendNowSuccess("");
  
    try {
      if (deliveryMode !== "now") {
        throw new Error("Select Send Now before continuing.");
      }
  
      if (!editingDraftId) {
        throw new Error(
          "Please select and save a draft before sending."
        );
      }
  
      const currentUser = auth.currentUser;
  
      if (!currentUser) {
        throw new Error("Please sign in again.");
      }
  
  
    // Verify the actual saved campaign, not unsaved editor changes.
    const savedCampaignSnapshot = await getDoc(
        doc(db, "notificationCampaigns", editingDraftId)
      );
  
      if (!savedCampaignSnapshot.exists()) {
        throw new Error(
          "The saved campaign no longer exists. Sending cancelled."
        );
      }
  
      const savedCampaign = savedCampaignSnapshot.data();
      const savedAudience = savedCampaign.audience || {};
  
      const approvedRecipientUid =
        "Q2gCkZrydJWCtjF5t02QDWRwzqR2";
  
      if (savedCampaign.status !== "draft") {
        throw new Error(
          "This campaign is no longer a draft. Sending cancelled."
        );
      }
  
      if (
        savedAudience.allUsers !== false ||
        savedAudience.smartCategory !== false ||
        savedAudience.individualUsers !== true ||
        !Array.isArray(savedAudience.selectedUserIds) ||
        savedAudience.selectedUserIds.length !== 1 ||
        savedAudience.selectedUserIds[0] !== approvedRecipientUid ||
        (savedAudience.selectedCategories || []).length !== 0 ||
        (savedAudience.excludedUserIds || []).length !== 0
      ) {
        throw new Error(
          "Saved campaign audience does not match the single approved test recipient. Sending cancelled."
        );
      }

      const confirmed = window.confirm(
        "FINAL CONFIRMATION — REAL NOTIFICATION\n\n" +
        "Saved campaign: " + (savedCampaign.campaignName || editingDraftId) + "\n" +
        "Approved recipient: porschey550@gmail.com\n" +
        "Recipients permitted: 1\n\n" +
        "Send this saved campaign now?"
      );

      if (!confirmed) {
        return;
      }

      const idToken = await getIdToken(currentUser);
  
      const response = await fetch(
        "https://us-central1-q8auctionapp-2c6dd.cloudfunctions.net/adminSendNotificationCampaign",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            campaignId: editingDraftId,
          }),
        }
      );
  
      const result = await response.json();
  
      if (!response.ok || result.ok !== true) {
        throw new Error(
          result.error || "Send Now could not be completed."
        );
      }
  
      setSendNowSuccess(
        "Campaign accepted for processing. Check delivery status before attempting another send."
      );
    } catch (error) {
      setSendNowError(
        error.message ||
        "Send Now failed. Check campaign status before retrying."
      );
    } finally {
      setSendNowProcessing(false);
    }
  };

const handleSaveDraft = async () => {
    if (draftSaving) return;
  
    if (deliveryMode !== "draft") {
      setDraftSaveSuccess("");
      setDraftSaveError(
        "Sending and scheduling are not enabled. Select Save Draft to continue."
      );
      return;
    }
  
    setDraftSaving(true);
    setDraftSaveError("");
    setDraftSaveSuccess("");

  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("Please sign in again.");
    }

    
    if (!campaignName.trim()) {
        throw new Error("Please enter a campaign name.");
      }
  
      if (categoriesLoading || categoriesError) {
        throw new Error(
          "Please wait until Firebase confirms the current category list before saving."
        );
      }
  
      const activeCategoryNames = new Set(
        officialCategories.map((category) => category.name)
      );
  
      const validSelectedCategories = selectedCategories.filter(
        (name) => activeCategoryNames.has(name)
      );
  
      const draftData = {
          status: "draft",
          campaignName: campaignName.trim(),
  
          audience: {
            allUsers: audience.allUsers,
            smartCategory: audience.smartCategory,
            individualUsers: audience.individualUsers,
            selectedCategories: validSelectedCategories,
            selectedUserIds,
            excludedUserIds,
          },
  
        notification: {
          englishTitle,
          englishBody,
          arabicTitle,
          arabicBody,
        },
  
        destination,
        selectedAuctionId,
        fullMessageEnglish,
        fullMessageArabic,
  
        deliveryMode,
        scheduledDate,
        scheduledTime,
  
        updatedAt: serverTimestamp(),
      };
  
      if (editingDraftId) {
        const campaignRef = doc(
          db,
          "notificationCampaigns",
          editingDraftId
        );

        await runTransaction(db, async (transaction) => {
          const campaignSnapshot = await transaction.get(campaignRef);

          if (!campaignSnapshot.exists()) {
            throw new Error(
              "This draft no longer exists. Please create a new draft."
            );
          }

          if (campaignSnapshot.data().status !== "draft") {
            throw new Error(
              "This campaign is no longer a draft and cannot be edited."
            );
          }

          transaction.update(campaignRef, draftData);
        });

        setDraftSaveSuccess("Draft updated successfully.");
      } else {
        const savedDraft = await addDoc(
          collection(db, "notificationCampaigns"),
          {
            ...draftData,
            createdByUid: currentUser.uid,
            createdAt: serverTimestamp(),
          }
        );
  
        setEditingDraftId(savedDraft.id);
  
        setDraftSaveSuccess(
          `Draft saved successfully. ID: ${savedDraft.id}`
        );
      }
  } catch (error) {
    setDraftSaveError(
      error.message || "Unable to save draft."
    );
  } finally {
    setDraftSaving(false);
}
};

const handleOpenDraft = (draft) => {
  if (draftSaving) return;

  setEditingDraftId(draft.id);
  setCampaignName(draft.campaignName || "");

  setAudience({
    allUsers: draft.audience?.allUsers === true,
    smartCategory: draft.audience?.smartCategory === true,
    individualUsers: draft.audience?.individualUsers === true,
  });

  setSelectedCategories(draft.audience?.selectedCategories || []);
  setSelectedUserIds(draft.audience?.selectedUserIds || []);
  setExcludedUserIds(draft.audience?.excludedUserIds || []);

  setEnglishTitle(draft.notification?.englishTitle || "");
  setEnglishBody(draft.notification?.englishBody || "");
  setArabicTitle(draft.notification?.arabicTitle || "");
  setArabicBody(draft.notification?.arabicBody || "");

  setDestination(draft.destination || "browse");
  setSelectedAuctionId(draft.selectedAuctionId || "");
  setFullMessageEnglish(draft.fullMessageEnglish || "");
  setFullMessageArabic(draft.fullMessageArabic || "");

  setDeliveryMode("draft");
  setScheduledDate(draft.scheduledDate || "");
  setScheduledTime(draft.scheduledTime || "");

  setDraftSaveError("");
  setDraftSaveSuccess("");

  requestAnimationFrame(() => {
    document
      .getElementById("notification-campaign-name")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
  });
};

const handleNewDraft = () => {
  if (draftSaving) return;

  setEditingDraftId(null);
  setCampaignName("");

  setAudience({
    allUsers: false,
    smartCategory: false,
    individualUsers: false,
  });

  setSelectedCategories([]);
  setSelectedUserIds([]);
  setExcludedUserIds([]);

  setEnglishTitle("");
  setEnglishBody("");
  setArabicTitle("");
  setArabicBody("");

  setDestination("browse");
  setSelectedAuctionId("");
  setFullMessageEnglish("");
  setFullMessageArabic("");

  setDeliveryMode("draft");
  setScheduledDate("");
  setScheduledTime("");

  setDraftSaveError("");
  setDraftSaveSuccess("");

  document
    .getElementById("notification-campaign-name")
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const handleDeleteDraft = async (draft) => {
  if (deletingDraftId || draftSaving) return;

  const confirmed = window.confirm(
    `Are you sure you want to permanently delete "${draft.campaignName || "Untitled Campaign"}"?\n\nThis action cannot be undone.`
  );

  if (!confirmed) return;

  setDeletingDraftId(draft.id);
  setDeleteDraftError("");

  try {
    const campaignRef = doc(
      db,
      "notificationCampaigns",
      draft.id
    );

    await runTransaction(db, async (transaction) => {
      const campaignSnapshot = await transaction.get(campaignRef);

      if (!campaignSnapshot.exists()) {
        throw new Error(
          "This draft no longer exists."
        );
      }

      if (campaignSnapshot.data().status !== "draft") {
        throw new Error(
          "This campaign is no longer a draft and cannot be deleted."
        );
      }

      transaction.delete(campaignRef);
    });

    if (editingDraftId === draft.id) {
      handleNewDraft();
    }
  } catch (error) {
    setDeleteDraftError(
      error.message || "Unable to delete draft."
    );
  } finally {
    setDeletingDraftId(null);
  }
};

return (
  <div>
    <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Notification Center</h2>
            <p>
              Create, schedule, and manage bilingual notifications
              for X-AUCTIONS users.
            </p>
          </div>
        </div>

        <h3>Campaign Composer</h3>
        <p>
          Prepare a notification campaign and select its recipients.
        </p>

        <h3>1. Select Audience</h3>
        <p>
          You can combine multiple audience types. Matching users
          will be counted only once.
        </p>

        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <label>
            <input
              type="checkbox"
              checked={audience.allUsers}
              onChange={() => toggleAudience("allUsers")}
            />
            {" "}All Users
          </label>

          <label>
            <input
              type="checkbox"
              checked={audience.smartCategory}
              onChange={() => toggleAudience("smartCategory")}
            />
            {" "}Smart Category Audience
          </label>

          <label>
            <input
              type="checkbox"
              checked={audience.individualUsers}
              onChange={() => toggleAudience("individualUsers")}
            />
            {" "}Individual Users
          </label>
        </div>

        {audience.allUsers && (
          <p>
            All eligible registered users will be included.
          </p>
        )}

{audience.smartCategory && (
          <div style={{ marginTop: 16 }}>
            <h4>Select Auction Categories</h4>
            <p>
              Choose one or more categories. The future targeting
              system will match users based on meaningful engagement
              with auctions in these categories.
            </p>

            {categoriesLoading && (
              <p style={{ marginTop: 12 }}>
                Loading official auction categories from Firebase...
              </p>
            )}

            {categoriesError && (
              <p style={{ marginTop: 12, color: "#dc2626" }}>
                {categoriesError}
              </p>
            )}

            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() =>
                    setSelectedCategories(
                      officialCategories.map((category) => category.name)
                    )
                  }
                  disabled={categoriesLoading || Boolean(categoriesError)}
              >
                Select All
              </button>

              <button
                type="button"
                onClick={() => setSelectedCategories([])}
              >
                Clear All
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              {officialCategories.map((category) => (
  <label key={category.id}>
    <input
      type="checkbox"
      checked={selectedCategories.includes(category.name)}
      disabled={categoriesLoading || Boolean(categoriesError)}
      onChange={() =>
        setSelectedCategories((previous) =>
          previous.includes(category.name)
            ? previous.filter((item) => item !== category.name)
            : [...previous, category.name]
        )
      }
    />
    {" "}{category.name}
  </label>
))}
            </div>

            <p style={{ marginTop: 16 }}>
  <strong>Selected Categories:</strong>{" "}
  {
    officialCategories.filter((category) =>
      selectedCategories.includes(category.name)
    ).length
  } of {officialCategories.length}
</p>
          </div>
        )}

{audience.individualUsers && (
          <div style={{ marginTop: 16 }}>
            <h4>Select Individual Users</h4>

            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <strong>
                Selected Recipients: {selectedUserIds.length}
              </strong>

              {selectedUserIds.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    marginTop: 12,
                  }}
                >
                  {selectedUserIds.map((userId) => {
                    const selectedUser = users.find(
                      (user) => user.id === userId
                    );

                    return (
                      <div
                        key={userId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: 12,
                          border: "1px solid #ddd",
                          borderRadius: 8,
                        }}
                      >
                        <div>
                          <strong>
                            {selectedUser?.username || "Unknown user"}
                          </strong>
                          <div>
                            {selectedUser?.email || "No email"}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            setSelectedUserIds((previous) =>
                              previous.filter((id) => id !== userId)
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <p>
              Search registered users by username or email
              to add them to this notification campaign.
            </p>

            <label
              htmlFor="notification-user-search"
              style={{ display: "block", marginTop: 16 }}
            >
              Search Users
            </label>

            <input
              id="notification-user-search"
              type="text"
              value={userSearch}
              onChange={(event) => setUserSearch(event.target.value)}
              placeholder="Search by username or email"
              style={{
                width: "100%",
                maxWidth: 600,
                padding: 10,
                marginTop: 8,
              }}
            />

            {normalizedUserSearch && (
              <div style={{ marginTop: 16 }}>
                <p>
                  <strong>Matching Users:</strong>{" "}
                  {matchingUsers.length}
                </p>

                {matchingUsers.length === 0 ? (
                  <p>No matching users found.</p>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gap: 8,
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    {matchingUsers.slice(0, 50).map((user) => (
                                            <label
                                            key={user.id}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 12,
                                              padding: 12,
                                              border: "1px solid #ddd",
                                              borderRadius: 8,
                                              cursor: "pointer",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedUserIds.includes(user.id)}
                                              onChange={() =>
                                                setSelectedUserIds((previous) =>
                                                  previous.includes(user.id)
                                                    ? previous.filter((id) => id !== user.id)
                                                    : [...previous, user.id]
                                                )
                                              }
                                            />
                    
                                            <div>
                                              <strong>
                                                {user.username || "No username"}
                                              </strong>
                                              <div>{user.email || "No email"}</div>
                                            </div>
                                          </label>
                    ))}
                  </div>
                )}

                {matchingUsers.length > 50 && (
                  <p>
                    Showing the first 50 matches. Refine your search
                    to find a specific user.
                  </p>
                )}
              </div>
            )}
                    </div>
        )}

        <div style={{ marginTop: 24 }}>
          <h4>Exclude Users</h4>

          <p>
            Search for users who should not receive this campaign,
            even if they match the selected audience.
          </p>

          <p>
            <strong>Excluded Users:</strong> {excludedUserIds.length}
          </p>

          {excludedUserIds.length > 0 && (
            <div
              style={{
                display: "grid",
                gap: 8,
                marginTop: 12,
                marginBottom: 20,
              }}
            >
              {excludedUserIds.map((userId) => {
                const excludedUser = users.find(
                  (user) => user.id === userId
                );

                return (
                  <div
                    key={userId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: 12,
                      border: "1px solid #ddd",
                      borderRadius: 8,
                    }}
                  >
                    <div>
                      <strong>
                        {excludedUser?.username || "Unknown user"}
                      </strong>
                      <div>
                        {excludedUser?.email || "No email"}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setExcludedUserIds((previous) =>
                          previous.filter((id) => id !== userId)
                        )
                      }
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <label
            htmlFor="notification-excluded-user-search"
            style={{ display: "block", marginTop: 16 }}
          >
            Search Users to Exclude
          </label>

          <input
            id="notification-excluded-user-search"
            type="text"
            value={excludedUserSearch}
            onChange={(event) =>
              setExcludedUserSearch(event.target.value)
            }
            placeholder="Search by username or email"
            style={{
              width: "100%",
              maxWidth: 600,
              padding: 10,
              marginTop: 8,
            }}
          />

          {normalizedExcludedUserSearch && (
            <div style={{ marginTop: 16 }}>
              <p>
                <strong>Matching Users:</strong>{" "}
                {matchingExcludedUsers.length}
              </p>

              {matchingExcludedUsers.length === 0 ? (
                <p>No matching users found.</p>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  {matchingExcludedUsers.slice(0, 50).map((user) => (
                    <label
                      key={user.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: 12,
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={excludedUserIds.includes(user.id)}
                        onChange={() =>
                          setExcludedUserIds((previous) =>
                            previous.includes(user.id)
                              ? previous.filter((id) => id !== user.id)
                              : [...previous, user.id]
                          )
                        }
                      />

                      <div>
                        <strong>
                          {user.username || "No username"}
                        </strong>
                        <div>{user.email || "No email"}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {matchingExcludedUsers.length > 50 && (
                <p>
                  Showing the first 50 matches. Refine your search
                  to find a specific user.
                </p>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 28 }}>
          <h3>2. Campaign Details</h3>

          {editingDraftId && (
            <div
              style={{
                marginTop: 16,
                padding: "12px 16px",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                background: "#f8fafc",
                color: "#334155",
              }}
            >
              <strong>Editing Draft:</strong>{" "}
              {savedDrafts.find((draft) => draft.id === editingDraftId)
                ?.campaignName || campaignName}

              <p style={{ margin: "6px 0 0", fontSize: 13 }}>
                Changes will be saved to this existing campaign
                when you click Update Draft.
              </p>
            </div>
          )}

          <p>
            Give this campaign an internal name to help identify
            it in campaign history.
          </p>

          <label
            htmlFor="notification-campaign-name"
            style={{ display: "block", marginTop: 16 }}
          >
            Campaign Name
          </label>
          <input
            id="notification-campaign-name"
            type="text"
            value={campaignName}
            onChange={(event) => setCampaignName(event.target.value)}
            placeholder="Example: Weekend Electronics Auctions"
            style={{ width: "100%", maxWidth: 600, padding: 10, marginTop: 8 }}
          />
        </div>

        <div style={{ marginTop: 28 }}>
          <h3>3. Notification Message</h3>
          <p>
            Both English and Arabic are required. Each user will
            eventually receive the version matching their app language.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
              marginTop: 16,
            }}
          >
            <div>
              <h4>English</h4>

              <label htmlFor="notification-en-title">Title</label>
              <input
                id="notification-en-title"
                type="text"
                value={englishTitle}
                onChange={(event) => setEnglishTitle(event.target.value)}
                placeholder="Notification title"
                maxLength={100}
                style={{ display: "block", width: "100%", padding: 10, marginTop: 8 }}
              />

              <label
                htmlFor="notification-en-body"
                style={{ display: "block", marginTop: 16 }}
              >
                Message
              </label>
              <textarea
                id="notification-en-body"
                value={englishBody}
                onChange={(event) => setEnglishBody(event.target.value)}
                placeholder="Write your English message..."
                rows={5}
                style={{ display: "block", width: "100%", padding: 10, marginTop: 8 }}
              />
            </div>

            <div>
              <h4>العربية</h4>

              <label htmlFor="notification-ar-title">العنوان</label>
              <input
                id="notification-ar-title"
                type="text"
                dir="rtl"
                value={arabicTitle}
                onChange={(event) => setArabicTitle(event.target.value)}
                placeholder="عنوان الإشعار"
                maxLength={100}
                style={{ display: "block", width: "100%", padding: 10, marginTop: 8 }}
              />

              <label
                htmlFor="notification-ar-body"
                style={{ display: "block", marginTop: 16 }}
              >
                الرسالة
              </label>
              <textarea
                id="notification-ar-body"
                dir="rtl"
                value={arabicBody}
                onChange={(event) => setArabicBody(event.target.value)}
                placeholder="اكتب الرسالة باللغة العربية..."
                rows={5}
                style={{ display: "block", width: "100%", padding: 10, marginTop: 8 }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <h3>4. Live Message Preview</h3>
          <p>
            Preview how your notification content will look in
            English and Arabic. These are visual previews only.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
              marginTop: 16,
            }}
          >
            <div
              style={{
                border: "1px solid #d8dde5",
                borderRadius: 12,
                padding: 18,
              }}
            >
              <strong>English Preview</strong>

              <div style={{ marginTop: 16 }}>
                <strong>
                  {englishTitle.trim() || "Notification title"}
                </strong>
                <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {englishBody.trim() || "Your English message will appear here."}
                </p>
              </div>
            </div>

            <div
              dir="rtl"
              style={{
                border: "1px solid #d8dde5",
                borderRadius: 12,
                padding: 18,
              }}
            >
              <strong>معاينة باللغة العربية</strong>

              <div style={{ marginTop: 16 }}>
                <strong>
                  {arabicTitle.trim() || "عنوان الإشعار"}
                </strong>
                <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {arabicBody.trim() || "ستظهر رسالتك العربية هنا."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <h3>5. Notification Destination</h3>
          <p>
            Choose what happens when a user opens this notification.
          </p>

          <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
            <label>
              <input
                type="radio"
                name="notification-destination"
                value="browse"
                checked={destination === "browse"}
                onChange={() => setDestination("browse")}
              />
              {" "}Browse Auctions
            </label>

            <label>
              <input
                type="radio"
                name="notification-destination"
                value="auction"
                checked={destination === "auction"}
                onChange={() => setDestination("auction")}
              />
              {" "}Auction Detail
            </label>

            <label>
              <input
                type="radio"
                name="notification-destination"
                value="message"
                checked={destination === "message"}
                onChange={() => setDestination("message")}
              />
              {" "}Full Message
            </label>
          </div>

          {destination === "browse" && (
            <p style={{ marginTop: 16 }}>
              Users will be directed to Browse Auctions.
            </p>
          )}

{destination === "auction" && (
            <div style={{ marginTop: 16 }}>
              <label
                htmlFor="notification-auction-search"
                style={{ display: "block", marginBottom: 8 }}
              >
                Search Auctions
              </label>

              <input
                id="notification-auction-search"
                type="text"
                value={auctionSearch}
                onChange={(event) => setAuctionSearch(event.target.value)}
                placeholder="Search by auction title or ID"
                style={{
                  width: "100%",
                  maxWidth: 600,
                  padding: 10,
                }}
              />

              <div
                style={{
                  marginTop: 12,
                  maxHeight: 260,
                  overflowY: "auto",
                  border: "1px solid #d8dde5",
                  borderRadius: 8,
                }}
              >
                {auctions
                  .filter((auction) => {
                    const query = auctionSearch.trim().toLowerCase();
                    const title = String(auction.title || "").toLowerCase();
                    const id = String(auction.id || "").toLowerCase();

                    return title.includes(query) || id.includes(query);
                  })
                  .slice(0, 30)
                  .map((auction) => (
                    <label
                      key={auction.id}
                      style={{
                        display: "block",
                        padding: 12,
                        borderBottom: "1px solid #e5e7eb",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="notification-auction-selection"
                        checked={selectedAuctionId === auction.id}
                        onChange={() => setSelectedAuctionId(auction.id)}
                      />
                      {" "}
                      <strong>{auction.title || "Untitled Auction"}</strong>
                      {" — "}
                      {auction.status || "Unknown status"}
                    </label>
                  ))}

                {auctions.length === 0 && (
                  <p style={{ padding: 12 }}>
                    No auctions are currently loaded.
                  </p>
                )}
              </div>

              {selectedAuctionId && (
                <p style={{ marginTop: 12 }}>
                  Selected Auction ID: <strong>{selectedAuctionId}</strong>
                </p>
              )}
            </div>
          )}

{destination === "message" && (
            <div style={{ marginTop: 20 }}>
              <h4>Full Message Content</h4>
              <p>
                Write the longer message users will see after
                opening the notification. Both languages are required.
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: 20,
                  marginTop: 16,
                }}
              >
                <div>
                  <label
                    htmlFor="notification-full-message-en"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    English Full Message
                  </label>

                  <textarea
                    id="notification-full-message-en"
                    value={fullMessageEnglish}
                    onChange={(event) =>
                      setFullMessageEnglish(event.target.value)
                    }
                    placeholder="Write the complete English announcement..."
                    rows={10}
                    style={{ width: "100%", padding: 12 }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="notification-full-message-ar"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    الرسالة الكاملة باللغة العربية
                  </label>

                  <textarea
                    id="notification-full-message-ar"
                    dir="rtl"
                    value={fullMessageArabic}
                    onChange={(event) =>
                      setFullMessageArabic(event.target.value)
                    }
                    placeholder="اكتب الإعلان الكامل باللغة العربية..."
                    rows={10}
                    style={{ width: "100%", padding: 12 }}
                  />
                </div>
              </div>

              <h4 style={{ marginTop: 24 }}>Full Message Preview</h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: 20,
                  marginTop: 16,
                }}
              >
                <div
                  style={{
                    border: "1px solid #d8dde5",
                    borderRadius: 12,
                    padding: 18,
                  }}
                >
                  <strong>
                    {englishTitle.trim() || "English title"}
                  </strong>

                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {fullMessageEnglish.trim() ||
                      "Your full English message will appear here."}
                  </p>
                </div>

                <div
                  dir="rtl"
                  style={{
                    border: "1px solid #d8dde5",
                    borderRadius: 12,
                    padding: 18,
                  }}
                >
                  <strong>
                    {arabicTitle.trim() || "العنوان العربي"}
                  </strong>

                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {fullMessageArabic.trim() ||
                      "ستظهر الرسالة العربية الكاملة هنا."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 28 }}>
          <h3>6. Delivery Options</h3>
          <p>
            Save your campaign as a draft, or preview the future
            sending and scheduling options. Sending and scheduling
            are not enabled yet.
          </p>

          <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
            <label>
              <input
                type="radio"
                name="notification-delivery-mode"
                checked={deliveryMode === "draft"}
                onChange={() => setDeliveryMode("draft")}
              />
              {" "}Save Draft
            </label>

            <label>
              <input
                type="radio"
                name="notification-delivery-mode"
                checked={deliveryMode === "now"}
                onChange={() => setDeliveryMode("now")}
              />
              {" "}Send Now
            </label>

            <label>
              <input
                type="radio"
                name="notification-delivery-mode"
                checked={deliveryMode === "schedule"}
                onChange={() => setDeliveryMode("schedule")}
              />
              {" "}Schedule
            </label>
          </div>

          {deliveryMode === "schedule" && (
            <div style={{ marginTop: 20 }}>
              <h4>Schedule Notification</h4>
              <p>
                All scheduled dates and times will use Kuwait time
                (UTC+3). Scheduling is not active yet.
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  marginTop: 12,
                }}
              >
                <div>
                  <label
                    htmlFor="notification-scheduled-date"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Date
                  </label>
                  <input
                    id="notification-scheduled-date"
                    type="date"
                    value={scheduledDate}
                    onChange={(event) =>
                      setScheduledDate(event.target.value)
                    }
                    style={{ padding: 10 }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="notification-scheduled-time"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Time (Kuwait)
                  </label>
                  <input
                    id="notification-scheduled-time"
                    type="time"
                    value={scheduledTime}
                    onChange={(event) =>
                      setScheduledTime(event.target.value)
                    }
                    style={{ padding: 10 }}
                  />
                </div>
              </div>
            </div>
          )}

{deliveryMode === "draft" && (
            <p style={{ marginTop: 16 }}>
              Save this campaign as a draft. You can reopen,
              edit, or delete saved drafts later.
            </p>
          )}

          {deliveryMode === "now" && (
            <p style={{ marginTop: 16 }}>
              This campaign will require recipient verification
              and final confirmation before sending.
            </p>
          )}
        </div>

        <div style={{ marginTop: 32 }}>
          <h3>7. Campaign Review</h3>
          <p>
            Review your campaign before saving, scheduling,
            or sending. This summary updates automatically.
          </p>

          <div
            style={{
              border: "1px solid #d8dde5",
              borderRadius: 12,
              padding: 20,
              marginTop: 16,
            }}
          >
            <h4>Campaign Details</h4>
            <p>
              <strong>Name:</strong>{" "}
              {campaignName.trim() || "Not entered"}
            </p>

            <h4>Selected Audience</h4>
            <p>
              {[
                audience.allUsers && "All Users",
                audience.smartCategory && "Smart Category Audience",
                audience.individualUsers && "Individual Users",
              ]
                .filter(Boolean)
                .join(", ") || "No audience selected"}
            </p>
            <p>
              <strong>Individually Selected:</strong>{" "}
              {audience.individualUsers ? selectedUserIds.length : 0}
            </p>

            <p>
              <strong>Excluded Users:</strong>{" "}
              {excludedUserIds.length}
            </p>

            <p>
              <strong>Individual Recipients After Exclusions:</strong>{" "}
              {audience.individualUsers
                ? selectedUserIds.filter(
                    (userId) => !excludedUserIds.includes(userId)
                  ).length
                : 0}
            </p>

            <p>
              <strong>Unique Recipients After Exclusions:</strong>{" "}
              {uniqueRecipientIds.length}
            </p>

            <p style={{ opacity: 0.7 }}>
              Preliminary count based on All Users and Individual Users,
              with duplicates and exclusions removed.
              Smart Category Audience is not included in this preliminary count.
            </p>

            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <button
                type="button"
                onClick={handlePreviewAudience}
                disabled={audiencePreviewLoading}
              >
                {audiencePreviewLoading
                  ? "Calculating Audience..."
                  : "Calculate Audience"}
              </button>

              {audiencePreviewCount !== null && (
                <p>
                  <strong>Backend-Calculated Recipients:</strong>{" "}
                  {audiencePreviewCount}
                </p>
              )}

              {audiencePreviewError && (
                <p style={{ color: "#dc2626" }}>
                  {audiencePreviewError}
                </p>
              )}

              <p style={{ opacity: 0.7 }}>
                Includes selected audience types, Smart Category matching,
                duplicate removal, and excluded users. This is a recipient
                estimate, not confirmation of notification delivery.
              </p>
            </div>

            <h4>English Notification</h4>
            <p>
              <strong>Title:</strong>{" "}
              {englishTitle.trim() || "Not entered"}
            </p>
            <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {englishBody.trim() || "No English message"}
            </p>

            <h4>Arabic Notification</h4>
            <div dir="rtl">
              <p>
                <strong>العنوان:</strong>{" "}
                {arabicTitle.trim() || "لم يتم إدخال عنوان"}
              </p>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {arabicBody.trim() || "لم يتم إدخال رسالة"}
              </p>
            </div>

            <h4>Destination</h4>
            <p>
              {destination === "browse"
                ? "Browse Auctions"
                : destination === "auction"
                ? "Auction Detail"
                : "Full Message"}
            </p>

            {destination === "auction" && (
              <p>
                <strong>Selected Auction:</strong>{" "}
                {selectedAuctionId || "No auction selected"}
              </p>
            )}

            {destination === "message" && (
              <div>
                <p>
                  <strong>English Full Message:</strong>{" "}
                  {fullMessageEnglish.trim()
                    ? "Entered"
                    : "Not entered"}
                </p>
                <p>
                  <strong>Arabic Full Message:</strong>{" "}
                  {fullMessageArabic.trim()
                    ? "Entered"
                    : "Not entered"}
                </p>
              </div>
            )}

            <h4>Delivery</h4>
            <p>
              {deliveryMode === "draft"
                ? "Save Draft"
                : deliveryMode === "now"
                ? "Send Now"
                : "Schedule"}
            </p>

            {deliveryMode === "schedule" && (
              <p>
                <strong>Scheduled for:</strong>{" "}
                {scheduledDate && scheduledTime
                  ? `${scheduledDate} at ${scheduledTime} (Kuwait time)`
                  : "Date or time missing"}
              </p>
            )}
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={draftSaving || deliveryMode !== "draft"}
            style={{
              padding: "12px 24px",
              borderRadius: 8,
              cursor:
                draftSaving || deliveryMode !== "draft"
                  ? "not-allowed"
                  : "pointer",
              opacity:
                draftSaving || deliveryMode !== "draft"
                  ? 0.5
                  : 1,
              fontWeight: 600,
            }}
            >
            {draftSaving
              ? "Saving Draft..."
              : editingDraftId
              ? "Update Draft"
              : "Save Draft"}
          </button>

          {deliveryMode === "now" && (
            <button
              type="button"
              onClick={handleSendNow}
              disabled={
                sendNowProcessing ||
                draftSaving ||
                testSending ||
                deletingDraftId !== null ||
                !editingDraftId ||
                audiencePreviewLoading ||
                audiencePreviewCount !== 1
              }
              style={{
                marginLeft: 12,
                padding: "12px 24px",
                borderRadius: 8,
                fontWeight: 600,
                cursor:
                  sendNowProcessing ||
                  draftSaving ||
                  testSending ||
                  deletingDraftId !== null ||
                  !editingDraftId ||
                  audiencePreviewLoading ||
                  audiencePreviewCount !== 1
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {sendNowProcessing
                ? "Processing Send Now..."
                : "Confirm Send Now"}
            </button>
          )}

          {sendNowError && (
            <p style={{ color: "#dc2626", marginTop: 12 }}>
              {sendNowError}
            </p>
          )}

          {sendNowSuccess && (
            <p style={{ color: "#16a34a", marginTop: 12 }}>
              {sendNowSuccess}
            </p>
          )}

          <button
            type="button"
            onClick={handleNewDraft}
            disabled={draftSaving}
            style={{
              marginLeft: 12,
              padding: "12px 24px",
              borderRadius: 8,
              cursor: draftSaving ? "not-allowed" : "pointer",
              opacity: draftSaving ? 0.5 : 1,
              fontWeight: 600,
            }}
          >
            New Draft
          </button>

          <button
            type="button"
            onClick={handleTestSend}
            disabled={
              testSending ||
              draftSaving ||
              deletingDraftId !== null ||
              !editingDraftId
            }
            title={
              !editingDraftId
                ? "Select a saved draft before testing"
                : "Test Send to your own account"
            }
            style={{
              marginLeft: 12,
              padding: "12px 24px",
              borderRadius: 8,
              cursor:
                testSending ||
                draftSaving ||
                deletingDraftId !== null ||
                !editingDraftId
                  ? "not-allowed"
                  : "pointer",
              opacity:
                testSending ||
                draftSaving ||
                deletingDraftId !== null ||
                !editingDraftId
                  ? 0.5
                  : 1,
              fontWeight: 600,
            }}
          >
            {testSending ? "Processing Test Send..." : "Test Send"}
          </button>

          {testSendError && (
            <p style={{ marginTop: 12, color: "#dc2626" }}>
              {testSendError}
            </p>
          )}

          {testSendSuccess && (
            <p style={{ marginTop: 12, color: "#16a34a" }}>
              {testSendSuccess}
            </p>
          )}

          {editingDraftId && (
            <p style={{ marginTop: 12, opacity: 0.7 }}>
              Test Send will use the currently selected saved draft
              and send only to your own account when enabled.
            </p>
          )}

          {draftSaveSuccess && (
            <p style={{ marginTop: 12, color: "#16a34a" }}>
              {draftSaveSuccess}
            </p>
          )}

          {draftSaveError && (
            <p style={{ marginTop: 12, color: "#dc2626" }}>
              {draftSaveError}
            </p>
          )}

<p style={{ marginTop: 16, opacity: 0.7 }}>
            Draft saving is available. Sending and scheduling
            notifications are not enabled yet.
          </p>
        </div>

        <div style={{ marginTop: 36 }}>
          <h3>Saved Drafts</h3>

          <p style={{ opacity: 0.7 }}>
            Previously saved notification campaigns.
            Newest drafts appear first.
          </p>

          {savedDraftsLoading ? (
            <p>Loading saved drafts...</p>
          ) : savedDraftsError ? (
            <p style={{ color: "#dc2626" }}>
              {savedDraftsError}
            </p>
          ) : savedDrafts.length === 0 ? (
            <p>No saved drafts yet.</p>
          ) : (
            <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
              {savedDrafts.map((draft) => (
                <div
                  key={draft.id}
                  style={{
                    padding: 16,
                    border: "1px solid #ddd",
                    borderRadius: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <strong>
                      {draft.campaignName || "Untitled Campaign"}
                    </strong>

                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 20,
                        background: "#e5e7eb",
                        color: "#374151",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Draft
                    </span>
                  </div>

                  <p style={{ marginTop: 10, opacity: 0.7 }}>
                    Created:{" "}
                    {draft.createdAt?.toDate
                      ? draft.createdAt.toDate().toLocaleString("en-KW", {
                          timeZone: "Asia/Kuwait",
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "Date unavailable"}
                  </p>

                  <button
                    type="button"
                    onClick={() => handleOpenDraft(draft)}
                    disabled={draftSaving || deletingDraftId !== null}
                    style={{
                      marginTop: 12,
                      padding: "9px 16px",
                      borderRadius: 8,
                      cursor:
                        draftSaving || deletingDraftId !== null
                          ? "not-allowed"
                          : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {editingDraftId === draft.id
                      ? "Currently Editing"
                      : "Open Draft"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteDraft(draft)}
                    disabled={draftSaving || deletingDraftId !== null}
                    style={{
                      marginTop: 12,
                      marginLeft: 10,
                      padding: "9px 16px",
                      borderRadius: 8,
                      border: "1px solid #dc2626",
                      background: "transparent",
                      color: "#dc2626",
                      cursor:
                        draftSaving || deletingDraftId !== null
                          ? "not-allowed"
                          : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {deletingDraftId === draft.id
                      ? "Deleting..."
                      : "Delete Draft"}
                  </button>
                </div>
              ))}
            </div>
          )}

{deleteDraftError && (
            <p style={{ marginTop: 12, color: "#dc2626" }}>
              {deleteDraftError}
            </p>
          )}
        </div>
      </section>

      <section
        style={{
          marginTop: 24,
          padding: 24,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#ffffff",
        }}
      >
        <h2 style={{ marginTop: 0 }}>
          Campaign History &amp; Delivery Status
        </h2>

        <p style={{ color: "#6b7280", fontSize: 14 }}>
          View campaign processing history and recorded delivery outcomes.
          Expo acceptance does not confirm delivery to a user's device.
        </p>

        {campaignHistoryLoading ? (
          <p>Loading campaign history...</p>
        ) : campaignHistoryError ? (
          <p style={{ color: "#dc2626" }}>
            {campaignHistoryError}
          </p>
        ) : campaignHistory.length === 0 ? (
          <p style={{ color: "#6b7280" }}>
            No campaign history available yet.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            {campaignHistory.map((campaign) => {
                            const isFinalSummary =
                            campaign.status === "sent" &&
                            campaign.deliverySummary != null;
            
                          const summary = isFinalSummary
                            ? campaign.deliverySummary
                            : campaign.deliveryProgress || {};

              const hasDeliveryTotals = [
                "expoAccepted",
                "outcomeUnknown",
                "historyOnly",
              ].every(
                (key) =>
                  Number.isSafeInteger(summary[key]) &&
                  summary[key] >= 0
              );

              const isInterrupted =
              campaign.status === "interrupted";

            const isCompleted =
              campaign.status === "sent";

            const isFailed =
              campaign.status === "failed";

              const formatDate = (timestamp) => {
                const date = timestamp?.toDate?.();
                return date instanceof Date &&
                  !Number.isNaN(date.getTime())
                  ? date.toLocaleString()
                  : "Not available";
              };

              return (
                <div
                  key={campaign.id}
                  style={{
                    padding: 18,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: "#f9fafb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    <strong>
  {campaign.campaignName ||
    campaign.title ||
    campaign.subject ||
    campaign.name ||
    "Untitled Campaign"}
</strong>

<span
                      style={{
                        fontWeight: 600,
                        color: isInterrupted || isFailed
                          ? "#b91c1c"
                          : isCompleted
                          ? "#166534"
                          : "#374151",
                      }}
                    >
                      {isCompleted
                        ? "Processing Completed"
                        : isInterrupted
                        ? "Interrupted"
                        : isFailed
                        ? "Failed"
                        : campaign.status || "Unknown"}
                    </span>
                  </div>

                  <p
                    style={{
                      marginBottom: 6,
                      fontSize: 13,
                      color: "#6b7280",
                    }}
                  >
                    Created: {formatDate(campaign.createdAt)}
                  </p>

                  {Number.isSafeInteger(campaign.scheduledForMs) &&
                    campaign.scheduledForMs > 0 &&
                    !Number.isNaN(
                      new Date(campaign.scheduledForMs).getTime()
                    ) && (
                      <p
                        style={{
                          marginTop: 4,
                          marginBottom: 6,
                          fontSize: 13,
                          color: "#6b7280",
                        }}
                      >
                        Scheduled for:{" "}
                        {new Date(
                          campaign.scheduledForMs
                        ).toLocaleString("en-GB", {
                          timeZone: "Asia/Kuwait",
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}{" "}
                        (Kuwait time)
                      </p>
                    )}

                  {isCompleted && (
                    <p
                      style={{
                        marginTop: 4,
                        fontSize: 13,
                        color: "#6b7280",
                      }}
                    >
                      Processing completed:{" "}
                      {formatDate(campaign.sendingCompletedAt)}
                    </p>
                  )}

                  {hasDeliveryTotals ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 20,
                        marginTop: 14,
                        fontSize: 14,
                      }}
                    >
                                                                  <span>
                        <strong>
                          {isFinalSummary
                            ? "Expo accepted:"
                            : "Expo accepted (recorded so far):"}
                        </strong>{" "}
                        {summary.expoAccepted}
                      </span>

                      <span>
                        <strong>
                          {isFinalSummary
                            ? "Outcome unknown:"
                            : "Outcome unknown (recorded so far):"}
                        </strong>{" "}
                        {summary.outcomeUnknown}
                      </span>

                      <span>
                        <strong>
                          {isFinalSummary
                            ? "History only:"
                            : "History only (recorded so far):"}
                        </strong>{" "}
                        {summary.historyOnly}
                      </span>
                    </div>
                  ) : (
                    <p
                      style={{
                        marginTop: 12,
                        fontSize: 13,
                        color: "#6b7280",
                      }}
                    >
                      Delivery statistics are not available.
                    </p>
                  )}

{isInterrupted && (
                    <div
                      style={{
                        marginTop: 14,
                        color: "#b91c1c",
                        fontSize: 14,
                      }}
                    >
                      <strong>
                        Manual investigation required.
                      </strong>

                      <p style={{ marginBottom: 0 }}>
                        {campaign.interruptionReason ||
                          "Campaign processing was interrupted. Push outcomes may be uncertain."}
                      </p>

                      <p style={{ marginBottom: 0 }}>
                        Automatic retry is not available.
                      </p>
                    </div>
                  )}

                  {isFailed && (
                    <div
                      style={{
                        marginTop: 14,
                        color: "#b91c1c",
                        fontSize: 14,
                      }}
                    >
                      <strong>
                        Campaign failed before sending.
                      </strong>

                      <p style={{ marginBottom: 0 }}>
                        {campaign.scheduledFailureReason ||
                          "Scheduled campaign preparation failed."}
                      </p>

                      <p style={{ marginBottom: 0 }}>
                        No notifications were sent. Automatic retry is not available.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}